import React, { KeyboardEvent, ChangeEvent, useCallback, useEffect, useState } from 'react';
import { GridFilterModel, GridFilterOperator, GridRowSelectionModel, GridSortModel, getGridDateOperators,getGridNumericOperators } from '@mui/x-data-grid-pro';
import { GridColDef, GridRenderCellParams, getGridSingleSelectOperators } from '@mui/x-data-grid';
import { Grid, TextField } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PageContainer } from '../../../components/atoms/PageContainer';
import { HorizontalAlignProps, InlineGroup } from '../../../components/atoms/InlineGroup';
import { Typography, TypographyVariantProps } from '../../../components/atoms/Typography';
import { ColumnSelectionItem } from '../../../components/templates/ColumnSelection';
import { CustomDataGrid } from '../../../components/layouts/CustomDataGrid';
import { GridContainer, Wrapper } from '../../../styled/common.styles';
import { Link } from '../../../components/atoms/Link';
import { PartListResponse } from '../../../@types/part.type';
import { useGetAllParts, useGetPartConditions } from '../../../queries/part-query';
import { checkListPagetFilterAvailability, formatDate } from '../../../utils/common';
import { ActionPermissions, CustomPermission, ListType, NewReportDataType, OverwriteReportDataType, PropReportDataType, ReportResponse, SizeProps } from '../../../@types';
import { Snackbar } from '../../../components/atoms/Snackbar';
import { SavedReportTypeList, GridFeatureModeConstant } from '../../../configs/enums';
import { useAddNewReport, useGetDefaultReportByReportType, useGetReportByCode, useUpdateReportByCode } from '../../../queries/report-query';
import { generateReportURL } from '../../../utils/report';
import { PrintLabel } from '../../../components/molecules/PintLabel';
import { PrintLabelEntityTypes } from '../../../@types/print-label.type';
import { PLATFORM_NAME } from '../../../configs/common';
import { TabHandler } from '../../../handlers/TabHandler';
import { TagCell } from '../../../components/molecules/TagCell';
import { EXCEPTION_STATUS_OPTIONS, partListColumnSelectionItems } from '../../../constants/part';
import { DropdownItem } from '../../../components/atoms/SelectMenu';
import { Button } from '../../../components/atoms/Button';
import { exceptionStatusColumnFilterOperators, getObfuscationData, getPartListInitialFilterModel, getPartLocationLinkByLocationType } from '../../../utils/part';
import { MultiPasteFilterInput } from '../../../components/atoms/MultiPasteFilterInput/MultiPasteFilterInput';
import { ISACTIVE_FILTER_DROPDOWN_ITEM_ARRAY } from '../../../constants/common';
import { isUserHasPermission } from '../../../configs/permissions';
import { useGetUserSystemPermissions } from '../../../queries/user-query';

export const PartList: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reportCode = searchParams && searchParams.get('savedReportCode');
  const location = searchParams.get('nodeName');

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [parts, setParts] = useState<PartListResponse>();
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'assetId', sort: 'desc' }]);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>([]);
  const [serialSearch, setSerialSearch] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');
  const [firstLoad, setFirstLoad] = useState<boolean>(false);
  const [conditionList, setConditionList] = useState<DropdownItem[]>([]);
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [permissions, setPermissions] = useState<CustomPermission[]>([]);

  const [reportName, setReportName] = useState('');
  const [reportViewCode, setReportViewCode] = useState<string>();
  const [reportColumnSelectionItems, setReportColumnSelectionItems] = useState<Array<ColumnSelectionItem>>();
  const [openToast, setOpenToast] = useState<boolean>(false);
  const [newReportData, setNewReportData] = useState<PropReportDataType>();
  const [savedReportMessage, setSavedReportMessage] = useState('');
  const [isDefault, setIsDefault] = useState<boolean>(false);
  const [fetchDataGrid, setFetchDataGrid] = useState<boolean>(false);
  const [listId, setListId] = useState<string>(() => {
    if (reportCode) return `part-list-${reportCode}`;
    return 'part-list';
  });

  const getUserPermissionsQuery = useGetUserSystemPermissions();
  const useAddNewReportQuery = useAddNewReport();
  const useOverwriteReportQuery = useUpdateReportByCode(reportCode || '');
  const defaultReportData = useGetDefaultReportByReportType(SavedReportTypeList.PartList);
  const { data: reportQueryData, refetch: refetchGetReportByCodeQuery } = useGetReportByCode(reportCode || '');
  const getAllParts = useGetAllParts(
    { filterModel, page, pageSize, sortModel },
    searchKeyword ? serialSearch : '',
    reportColumnSelectionItems || partListColumnSelectionItems,
    getObfuscationData(defaultReportData?.data || [], reportQueryData)
  );
  const getPartConditions = useGetPartConditions();

  // Custom "isanyof" operator to paste list or series of values to search
  const customIsAnyOfOperator: GridFilterOperator = {
    ...getGridNumericOperators().find((op) => op.value === 'isAnyOf')!,
    InputComponent: MultiPasteFilterInput,
    InputComponentProps: { type: 'string' }
  };

  useEffect(() => {
    setFirstLoad(true);
  }, []);

  useEffect(() => {
    if (reportCode) setListId(`part-list-${reportCode}`);
    else setListId('part-list');
  }, [reportCode]);

  useEffect(() => {
    if (!reportCode) {
      setFilterModel({
        items: getPartListInitialFilterModel(location)
      });
    }
  }, [reportCode]);

  useEffect(() => {
    getUserPermissionsQuery.refetch();
  }, []);

  useEffect(() => {
    getUserPermissionsQuery.data && setPermissions(getUserPermissionsQuery.data);
  }, [getUserPermissionsQuery.data]);

  useEffect(() => {
    if (getPartConditions.data) {
      const conditionItems = getPartConditions?.data?.map((item) => ({
        value: item.value,
        label: item.label
      })) || [];
      
      setConditionList(conditionItems);
    }
  }, [getPartConditions.data, firstLoad]);

  useEffect(() => {
    setColumns([
      {
        field: 'assetId',
        headerName: 'Asset ID',
        width: 80,
        type: 'number',
        valueFormatter: (value: number) => value?.toString(),
        valueGetter: (_value, row) => row.assetId
      },
      {
        field: 'serial1',
        headerName: 'Serial 1',
        renderCell: (params: GridRenderCellParams) => params.row.id && <Link href={`/parts/${params.row.id}`}>{params.row.serial1}</Link>,
        type: 'string',
        width: 200,
        filterOperators: [
          ...getGridNumericOperators().filter((op) => op.value !== 'isAnyOf'),
          customIsAnyOfOperator,
        ],
        valueGetter: (_value, row) => row.serial1
      },
      {
        field: 'serial2',
        headerName: 'Serial 2',
        type: 'string',
        width: 120,
        filterOperators: [
          ...getGridNumericOperators().filter((op) => op.value !== 'isAnyOf'),
          customIsAnyOfOperator,
        ],
        valueGetter: (_value, row) => row.serial2
      },
      {
        field: 'serial3',
        headerName: 'Serial 3',
        type: 'string',
        width: 120,
        filterOperators: [
          ...getGridNumericOperators().filter((op) => op.value !== 'isAnyOf'),
          customIsAnyOfOperator,
        ],
        valueGetter: (_value, row) => row.serial3
      },
      {
        field: 'contract',
        headerName: 'Contract(s)',
        width: 250,
        valueGetter: (_value, row) => row.contract
      },
      {
        field: 'conditionName',
        headerName: 'Condition',
        type: 'singleSelect',
        valueOptions: conditionList,
        width: 120,
        valueGetter: (_value, row) => row.partCondition?.name?.toLowerCase(),
        valueFormatter: (_value, row) => row.partCondition?.name?.replace(/-/g, ' ')
      },
      {
        field: 'timeInCondition',
        headerName: 'Time in Condition',
        type: 'string',
        width: 150,
        filterable: false,
        sortable: false,
        valueGetter: (_value, row) => row.timeInCondition
      },
      {
        field: 'softwareVersion',
        headerName: 'Software Ver',
        type: 'string',
        width: 200,
        valueGetter: (_value, row) => row.softwareVersion
      },
      {
        field: 'firmwareVersion',
        headerName: 'Firmware Ver',
        type: 'string',
        width: 200,
        valueGetter: (_value, row) => row.firmwareVersion
      },
      {
        field: 'hardwareVersion',
        headerName: 'Hardware Rev',
        type: 'string',
        width: 200,
        valueGetter: (_value, row) => row.hardwareVersion
      },
      {
        field: 'fleetTagName',
        headerName: 'Fleet Tag',
        type: 'string',
        width: 100,
        valueGetter: (_value, row) => row.fleetTagName
      },
      {
        field: 'warrantyEnd',
        headerName: 'Warranty End',
        type: 'date',
        width: 150,
        valueGetter: (_value, row) => row?.warrantyEnd && new Date(row.warrantyEnd),
        valueFormatter: (value: string) => formatDate(value),
        filterOperators: getGridDateOperators().filter(
          (operator) => operator.value !== 'isNotEmpty' && operator.value !== 'isEmpty'
        )
      },
      {
        field: 'serialised',
        headerName: 'Serialised/Consumable',
        type: 'singleSelect',
        valueOptions: [
          { value: true, label: 'Serialised / Consumable' },
          { value: 'Serialised', label: 'Serialised' },
          { value: 'Consumable', label: 'Consumable' }
        ],
        width: 170,
        valueGetter: (_value, row) => row.serialised
      },
      {
        field: 'tags',
        headerName: 'Tags',
        width: 300,
        renderCell: (params: GridRenderCellParams) => {
          if (params.row.tags)
            return (
              <TagCell tags={JSON.parse(params.row.tags)} />
            );
          else
            return '';
        },
      },
      {
        field: 'quantityInStock',
        headerName: 'Qty',
        width: 100,
        type: 'number',
        valueFormatter: (value: number) => value?.toString(),
        valueGetter: (_value, row) => row.quantityInStock
      },
      {
        field: 'notes',
        headerName: 'Notes',
        type: 'string',
        width: 150,
        valueGetter: (_value, row) => row.notes
      },
      {
        field: 'createdAt',
        headerName: 'Created At',
        type: 'date',
        width: 180,
        valueGetter: (_value, row) => new Date(row.createdAt),
        valueFormatter: (value: string) => formatDate(value)
      },
      {
        field: 'createdByName',
        headerName: 'Created By',
        type: 'string',
        width: 150,
        valueGetter: (_value, row) => row.createdByName
      },
      {
        field: 'updatedAt',
        headerName: 'Updated At',
        type: 'date',
        width: 180,
        valueGetter: (value, row) => row?.updatedAt && new Date(row.updatedAt),
        valueFormatter: (value: string) => formatDate(value)
      },
      {
        field: 'updatedByName',
        headerName: 'Updated By',
        type: 'string',
        width: 150,
        valueGetter: (_value, row) => row.updatedByName
      },
      {
        field: 'partTypeId',
        headerName: 'Part Type ID',
        type: 'number',
        width: 100,
        valueGetter: (_value, row) => row.partTypeId,
        valueFormatter: (value: number) => value?.toString(),
      },
      {
        field: 'partTypeName',
        headerName: 'Part Type Name',
        type: 'string',
        width: 200,
        valueGetter: (_value, row) => row.partTypeName
      },
      {
        field: 'partTypeNumber',
        headerName: 'Part No.',
        type: 'string',
        width: 200,
        valueGetter: (_value, row) => row.partTypeNumber
      },
      {
        field: 'partTypeTags',
        headerName: 'Part Type Tags',
        width: 300,
        renderCell: (params: GridRenderCellParams) => {
          if (params.row.partTypeTags)
            return (
              <TagCell tags={JSON.parse(params.row.partTypeTags)} />
            );
          else
            return '';
        },
      },
      {
        field: 'partTypeIsActive',
        headerName: 'Part Type Active',
        type: 'string',
        width: 150,
        valueGetter: (value, row) => getPropertyTypeValue(row.partTypeIsActive)
      },
      {
        field: 'client',
        headerName: 'Client',
        width: 100,
        valueGetter: (_value, row) => row.client
      },
      {
        field: 'location',
        headerName: 'Location',
        width: 550,
        sortable: false,
        valueGetter: (_value, row) => row.location,
        renderCell: (params: GridRenderCellParams) => getPartLocationLinks(params.row.location, params.row.locationIds, params.row.locationNodeTypes)
      },
      {
        field: 'nodeId',
        headerName: 'Node ID',
        width: 100,
        type: 'number',
        valueGetter: (_value, row) => row.nodeId
      },
      {
        field: 'nodeName',
        headerName: 'Node Name',
        width: 150,
        valueGetter: (_value, row) => row.nodeName
      },
      {
        field: 'nodeTypeName',
        headerName: 'Node Type',
        width: 120,
        valueGetter: (_value, row) => row.nodeTypeName
      },
      {
        field: 'timeInLocation',
        headerName: 'Time in Location',
        width: 150,
        filterable: false,
        sortable: false,
        valueGetter: (_value, row) => row.timeInLocation
      },
      {
        field: 'partActionLocation',
        headerName: 'Location - Last Out Part Action',
        width: 230,
        filterable: false,
        sortable: false,
        valueGetter: (_value, row) => row.partActionLocation
      },
      {
        field: 'printLabel', 
        headerName: '', 
        renderCell: (params: GridRenderCellParams) => ( 
          <PrintLabel entityId={Number(params.row.assetId) || 0} entityType={PrintLabelEntityTypes.Part}/>
        ),
        filterable: false,
        sortable: false
      },
      {
        field: 'locationTypeCode',
        headerName: 'Parent Type Code',
        sortable: false,
        type: 'singleSelect',
        valueOptions: [
          { value: 'nodeOrPart', label: 'Node/Part' },
          { value: 'node', label: 'Node' },
          { value: 'part', label: 'Part' }
        ],
        filterOperators: getGridSingleSelectOperators().filter(
          (operator) => operator.value === 'is'
        )
      },
      {
        field: 'exceptionStatus',
        headerName: 'Exception Status',
        width: 140,
        sortable: false,
        type: 'singleSelect',
        filterOperators: exceptionStatusColumnFilterOperators(),
        valueOptions: EXCEPTION_STATUS_OPTIONS
      },
      {
        field: 'exceptionServiceId',
        headerName: 'Exception Service ID',
        width: 160,
        valueGetter: (_value, row) => row.exceptionServiceId,
        renderCell: (params: GridRenderCellParams) =>
          isUserHasPermission(ActionPermissions.Service_Edit_View_Service, getUserPermissionsQuery.data) ?
            <Link href={`services/${params.row.exceptionServiceId}`}>{params.row.exceptionServiceId}</Link> :
            params.row.exceptionServiceId
      },
      {
        field: 'exceptionResultNote',
        headerName: 'Exception Result Note',
        type: 'string',
        width: 300,
        valueGetter: (_value, row) => row.exceptionResultNote
      },
      {
        field: 'isActive',
        headerName: 'Active',
        type: 'singleSelect',
        width: 150,
        valueOptions: ISACTIVE_FILTER_DROPDOWN_ITEM_ARRAY,
        valueFormatter: (_value, row) => row.isActive ? 'Yes' : 'No'
      },
    ]);
  }, [permissions, conditionList]);

  TabHandler(`List Parts | ${PLATFORM_NAME}`);

  useEffect(() => {
    getAllParts.data && setParts(getAllParts.data);
  }, [getAllParts.data]);

  useEffect(() => {
    !reportCode && defaultReportData.refetch();
  }, [SavedReportTypeList.PartList]);

  useEffect(() => {
    if (!reportCode && defaultReportData.data && defaultReportData.data?.length > 0 && !location) {
      sessionStorage.setItem('part-list', JSON.stringify(defaultReportData.data[0].dataGridProperties?.filterModel));
      setFilterModel(defaultReportData.data[0].dataGridProperties?.filterModel);
      setSortModel(defaultReportData.data[0].dataGridProperties?.sortModel || []);
      setReportColumnSelectionItems(defaultReportData.data[0].columnSelection);
      setPageSize(defaultReportData.data[0].dataGridProperties?.pageSize);
      if (defaultReportData.data[0].dataGridProperties?.columnOrder && columns) {
        const updatedColumns = defaultReportData.data[0].dataGridProperties?.columnOrder.map((orderCol) => {
          const originalColumn = columns.find((col) => col.field === orderCol.field);
          return originalColumn ? { ...originalColumn } : orderCol;
        });
        setColumns(updatedColumns);
      }
    }
  }, [defaultReportData.data, reportCode]);

  useEffect(() => {
    if (reportCode) {
      refetchGetReportByCodeQuery();
      setReportViewCode(reportCode);
    }
  }, [reportCode, fetchDataGrid]);

  useEffect(() => {
    if (reportCode && reportQueryData && firstLoad) {
      document.title = `${reportQueryData.name} (${reportCode}) | List Parts | ${PLATFORM_NAME}`;

      const storedFilterModel = sessionStorage.getItem(`part-list-${reportCode}`);
      setFilterModel(storedFilterModel ? JSON.parse(storedFilterModel) : reportQueryData.dataGridProperties.filterModel);
      setSortModel(reportQueryData.dataGridProperties?.sortModel || []);
      setReportColumnSelectionItems(reportQueryData.columnSelection);
      setPageSize(reportQueryData.dataGridProperties.pageSize);
      setReportName(reportQueryData.name);
      setIsDefault(reportQueryData.isDefault);
      if (reportQueryData.dataGridProperties?.columnOrder && columns) {
        const updatedColumns = reportQueryData.dataGridProperties?.columnOrder.map((orderCol) => {
          const originalColumn = columns.find((col) => col.field === orderCol.field);
          return originalColumn ? { ...originalColumn } : orderCol;
        });
        setColumns(updatedColumns);
      }
      setFirstLoad(false);
    }
  }, [reportQueryData, reportViewCode, columns, firstLoad]);

  useEffect(() => {
    setNewReportData({
      dataGridProperties: {
        filterModel: filterModel,
        pageSize: pageSize,
        sortModel: sortModel || [],
        columnOrder: columns
      },
      reportTypeName: SavedReportTypeList.PartList,
      reportName: reportName,
      isDefault: reportQueryData?.isDefault,
      id: reportQueryData?.id,
      reportOwner: reportQueryData?.createdBy,
      columnSelection: reportColumnSelectionItems,
      reportProperties: reportQueryData?.reportProperties
    });
  }, [filterModel, pageSize, isDefault, reportName, sortModel, columns, reportQueryData?.reportProperties]);

  useEffect(() => {
    if(serialSearch || filterModel.items.filter(item => item.field !== 'locationTypeCode').length > 0 && checkListPagetFilterAvailability(filterModel) ) {
      getAllParts.refetch();
    }
  }, [page, pageSize, filterModel, sortModel, serialSearch, reportColumnSelectionItems]);

  useEffect(() => {
    if (reportCode && reportQueryData) {
      getAllParts.refetch();
    }
  }, [isDefault, reportCode, reportQueryData]);

  const handleColumnsChange = (items: Array<ColumnSelectionItem>) => {
    setReportColumnSelectionItems(items);
  };

  const savedReport = async (newReportData: NewReportDataType) => {
    setSavedReportMessage('');
    const response: ReportResponse = await useAddNewReportQuery.mutateAsync(newReportData);
    setSavedReportMessage(response?.message ? response?.message : 'Successfully Created');
    const reportURL = generateReportURL(window.location.pathname, response.code);
    setOpenToast(true);
    navigate(reportURL, { replace: true });
  };

  const overwriteReport = async (overWriteReportData: OverwriteReportDataType) => {
    setSavedReportMessage('');
    const response: ReportResponse = await useOverwriteReportQuery.mutateAsync(overWriteReportData);
    setSavedReportMessage(response?.message ? response?.message : 'Successfully Updated');
    const reportURL = generateReportURL(window.location.pathname, response.code);
    setOpenToast(true);
    navigate(reportURL, { replace: true });
  };

  const onFilterChange = useCallback((newFilterModel: GridFilterModel) => {
    setFilterModel({ ...newFilterModel });
  }, []);

  const handleSortModelChange = useCallback((sortModel: GridSortModel) => {
    setSortModel(sortModel);
  }, []);

  const handleSelectionModelChange = useCallback((selectionModel: GridRowSelectionModel) => {
    setSelectionModel(selectionModel);
  }, []);

  const getPropertyTypeValue = (propertyType: boolean) => {
    return propertyType ? 'Yes' : 'No';
  };

  const handleSerialSearch = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      setSerialSearch(searchKeyword);
    }
  };

  const getPartLocationLinks = (locationData: string, locationIdData: string, locationNodeTypesData: string) => {
    if (!locationData || locationData === 'Amtek') return <Grid>{locationData}</Grid>;

    const locations = locationData?.split(' / ');
    const locationIds = locationIdData?.split(' / ');
    const locationNodeTypes = locationNodeTypesData?.split(' / ');
    const links = 
    <Grid container>
      {locations?.map((location, index) => (
        <Grid key={index}><Link href={getPartLocationLinkByLocationType(locationNodeTypes[index], locationIds[index])}>{location}</Link>{index !== locations.length - 1 && ' /'}&nbsp;</Grid>
      ))}
    </Grid>;
    
    return links;
  };

  return (
    <PageContainer>
      <Wrapper>
        <InlineGroup horizontal={HorizontalAlignProps.Between}>
          <Typography variant={TypographyVariantProps.H5} fontWeight={600}>
            List Parts
          </Typography>
          <Grid container alignItems="center" justifyContent="flex-end" spacing={2} size={4}>
            <Grid>
              <TextField
                size={SizeProps.Small}
                placeholder="Serial search"
                value={searchKeyword}
                onChange={(e: ChangeEvent<HTMLInputElement>) => setSearchKeyword(e.target.value.replace(/\s/g, ''))}
                onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => handleSerialSearch(e)}
                autoFocus={true}
              />
            </Grid>
            <Grid>
              <Button onClick={() => setSerialSearch(searchKeyword)}>Search</Button>
            </Grid>
          </Grid>
        </InlineGroup>
        <GridContainer>
          <Grid>
            <Snackbar
              open={openToast}
              autoHideDuration={4000}
              message={savedReportMessage ? savedReportMessage : 'Successfully Created'}
              onClose={() => setOpenToast(false)}
            />
          </Grid>
          <CustomDataGrid
            id={listId}
            columns={columns}
            rows={parts?.data || []}
            rowCount={parts?.total || 0}
            exportFileName={ListType.PartList}
            enableCSVExport={
              (
                newReportData?.reportProperties?.obfuscation?.obfuscate &&
                (
                  isUserHasPermission(ActionPermissions.Part_Export, permissions) ||
                  isUserHasPermission(ActionPermissions.Part_Can_Export_Raw_Unobfuscated_Data, permissions)
                )
              ) ||
              (
                !newReportData?.reportProperties?.obfuscation?.obfuscate &&
                isUserHasPermission(ActionPermissions.Part_Export, permissions)
              )
            }
            columnSelectionItems={reportColumnSelectionItems ? reportColumnSelectionItems : partListColumnSelectionItems}
            filterMode={GridFeatureModeConstant.server}
            onFilterModelChange={(filterModel: GridFilterModel) => {
              onFilterChange(filterModel);
            }}
            loading={getAllParts.isFetching}
            rowsPerPageOptions={[25, 50, 100]}
            pagination={true}
            page={page}
            pageSize={pageSize}
            paginationMode={GridFeatureModeConstant.server}
            onPageChange={(newPage) => {
              setPage(newPage);
            }}
            onPageSizeChange={(newPageSize) => {
              setPageSize(newPageSize);
            }}
            sortModel={sortModel}
            sortingMode={GridFeatureModeConstant.server}
            onSortModelChange={handleSortModelChange}
            selectionModel={selectionModel}
            onSelectionModelChange={handleSelectionModelChange}
            initialState={{
              columns: {
                columnVisibilityModel: {
                  locationTypeCode: false
                },
              },
            }}
            componentsProps={{
              toolbar: {
                printOptions: { disableToolbarButton: true }
              }
            }}
            filterModel={filterModel}
            getRowId={(row) => row?.id}
            newReportData={newReportData}
            handleSavedReport={(newReportData: NewReportDataType) => savedReport(newReportData)}
            handleOverwriteReport={(overWriteReportData: OverwriteReportDataType) => overwriteReport(overWriteReportData)}
            enableSavedReport
            reportCode={reportViewCode}
            handleColumnsChange={handleColumnsChange}
            handleRefetch={() => setFetchDataGrid(!fetchDataGrid)}
            getRowHeight={() => 'auto'}
            serialSearch={serialSearch}
            setColumns={setColumns}
            showUnobfuscateExport={
              isUserHasPermission(ActionPermissions.Part_Can_Export_Raw_Unobfuscated_Data, permissions) &&
              newReportData?.reportProperties?.obfuscation?.obfuscate
            }
            hasExportPermission={isUserHasPermission(ActionPermissions.Part_Export, permissions)}
          />
        </GridContainer>
      </Wrapper>
    </PageContainer>
  );
};